import React, { useMemo, useState, useEffect } from 'react';
import { TipTapEditorOptimized } from '@/components/editor/TipTapEditorOptimized';
import { TipTapEditorWithResize } from '@/components/editor/TipTapEditorWithResize';
import { EditorToolbar } from '@/components/editor/EditorToolbar';
import { Editor } from '@tiptap/react';
import { useContentConverter } from '@/hooks/content-generator/use-content-converter';

interface PageContentEditorProps {
  content: string;
  onContentChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  onEditorReady?: (editor: Editor) => void;
  workbookId?: string;
  pageId?: string;
}

const PageContentEditor = React.memo(function PageContentEditor({
  content,
  onContentChange,
  placeholder = "Start writing your document content...",
  className = "",
  onEditorReady,
  workbookId,
  pageId,
}: PageContentEditorProps) {
  const { ensureCompatible, debugConversion } = useContentConverter();
  
  // Ensure content is in TipTap-compatible format
  const processedContent = useMemo(() => {
    if (!content) return '';
    
    // Convert to TipTap format if needed
    const converted = ensureCompatible(content);
    
    // Log conversion info for debugging
    if (content !== converted) {
      const debug = debugConversion(content);
      console.log('[PageContentEditor] Content converted for TipTap:', {
        originalType: debug.originalType,
        originalLength: debug.originalLength,
        convertedLength: debug.convertedLength,
        wasConverted: debug.wasConverted
      });
    }
    
    return converted;
  }, [content, ensureCompatible, debugConversion]);

  const [editor, setEditor] = useState<Editor | null>(null);

  return (
    <div className={`w-full ${className} relative`}>
      {/* Custom toolbar when workbook context is available */}
      {workbookId && editor && (
        <EditorToolbar 
          editor={editor} 
          workbookId={workbookId}
          pageId={pageId}
        />
      )}
      
      <TipTapEditorWithResize
        content={processedContent}
        onChange={onContentChange}
        placeholder={placeholder}
        className="h-full"
        toolbar={!workbookId} // Only show default toolbar if no workbook context
        bubbleMenu={true}
        editable={true}
        autoFocus={false}
        onEditorReady={(editorInstance) => {
          setEditor(editorInstance);
          onEditorReady?.(editorInstance);
        }}
        removeTopRadius={workbookId ? true : false} // Remove top radius when we have custom toolbar
        workbookId={workbookId}
        pageId={pageId}
      />
    </div>
  );
});

export default PageContentEditor; 